import axiosHttp from "services/axiosHttp";
import { API_BASE_URL } from "configs/AppConfig";

const { createSlice, createAsyncThunk } = require("@reduxjs/toolkit");

export const fetchLeadList = createAsyncThunk(
	"fetchLeadList",
	async ({ filters, page, limit }, { rejectWithValue }) => {
		const params = new URLSearchParams({
			page: page,
			limit: limit,
			keyword: filters?.keyword ?? "",
			categoryId: filters?.categoryId ?? "",
			projectTypeId: filters?.projectTypeId ?? "",
			stageId: filters?.stageId ?? "",
			statusId: filters?.statusId ?? "",
			outcomeId: filters?.outcomeId ?? "",
		});

		try {
			const response = await axiosHttp.get(`leads?${params}`);
			return response.data;
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || "Something went wrong");
		}
	}
);

export const fetchLeadDetail = createAsyncThunk(
	"fetchLeadDetail",
	async ({ id }, { rejectWithValue }) => {
		try {
			const response = await axiosHttp.get(`${API_BASE_URL}backoffice/lead/${id}`);
			return response.data;
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || "Something went wrong");
		}
	}
);

export const assignLeadTeam = createAsyncThunk(
	"assignLeadTeam",
	async ({ leadId, team }, { rejectWithValue }) => {
		try {
			const response = await axiosHttp.patch(`${API_BASE_URL}backoffice/leads/${leadId}/team`, { team });
			return response.data;
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || "Failed to assign team members");
		}
	}
);

export const fetchLeadCategories = createAsyncThunk(
	"fetchLeadCategories",
	async ({ identifier }, { rejectWithValue }) => {
		try {
			const response = await axiosHttp.get(`channel-core/categories?identifier=${identifier}`);
			return response.data;
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || "Failed to fetch categories");
		}
	}
);

export const fetchLeadStatuses = createAsyncThunk(
	"fetchLeadStatuses",
	async ({ identifier }, { rejectWithValue }) => {
		try {
			const response = await axiosHttp.get(`channel-core/statuses?identifier=${identifier}`);
			return response.data;
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || "Failed to fetch statuses");
		}
	}
);

const LeadSlice = createSlice({
	name: "LeadSlice",
	initialState: {
		isLoading: false,
		data: null,
		error: null,
		page: 1,
		hasMore: false,
		counts: null,
		leadDetail: null,
		isLoadingDetail: false,
		errorDetail: null,
		isAssigningTeam: false,
		errorAssignTeam: null,
		categories: null,
		isLoadingCategories: false,
		errorCategories: null,
		statuses: null,
		isLoadingStatuses: false,
		errorStatuses: null,
	},

	extraReducers: (builder) => {
		builder.addCase(fetchLeadList.pending, (state) => {
			state.isLoading = true;
			state.error = null;
		});

		builder.addCase(fetchLeadList.fulfilled, (state, action) => {
			state.isLoading = false;
			state.data = action.payload.data;
			state.page = action.payload.currentPage;
			state.hasMore = action.payload.hasMore;
			state.counts = action.payload.counts;
		});

		builder.addCase(fetchLeadList.rejected, (state, action) => {
			state.isLoading = false;
			if (action.payload) {
				state.error = action.payload;
			} else if (action.error.message) {
				state.error = action.error.message;
			} else {
				state.error = "Unknown error occurred";
			}
		});

		builder.addCase(fetchLeadDetail.pending, (state) => {
			state.isLoadingDetail = true;
			state.errorDetail = null;
		});

		builder.addCase(fetchLeadDetail.fulfilled, (state, action) => {
			state.isLoadingDetail = false;
			state.leadDetail = action.payload.data;
		});

		builder.addCase(fetchLeadDetail.rejected, (state, action) => {
			state.isLoadingDetail = false;
			if (action.payload) {
				state.errorDetail = action.payload;
			} else if (action.error.message) {
				state.errorDetail = action.error.message;
			} else {
				state.errorDetail = "Unknown error occurred";
			}
		});

		// Fetch Lead Categories
		builder.addCase(fetchLeadCategories.pending, (state) => {
			state.isLoadingCategories = true;
			state.errorCategories = null;
		});

		builder.addCase(fetchLeadCategories.fulfilled, (state, action) => {
			state.isLoadingCategories = false;
			state.categories = action.payload;
		});

		builder.addCase(fetchLeadCategories.rejected, (state, action) => {
			state.isLoadingCategories = false;
			state.errorCategories = action.payload || "Failed to fetch categories";
		});

		// Fetch Lead Statuses
		builder.addCase(fetchLeadStatuses.pending, (state) => {
			state.isLoadingStatuses = true;
			state.errorStatuses = null;
		});

		builder.addCase(fetchLeadStatuses.fulfilled, (state, action) => {
			state.isLoadingStatuses = false;
			state.statuses = action.payload;
		});

		builder.addCase(fetchLeadStatuses.rejected, (state, action) => {
			state.isLoadingStatuses = false;
			state.errorStatuses = action.payload || "Failed to fetch statuses";
		});
	},
});

export default LeadSlice.reducer;
